<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';

import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import AgentCapacityPolicyCard from 'dashboard/components-next/AssignmentPolicy/AgentCapacityPolicyCard/AgentCapacityPolicyCard.vue';
import ConfirmDeletePolicyDialog from './components/ConfirmDeletePolicyDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { isStacklab } = useConfig();
const autopilotDialogRef = ref(null);
const recommendations = ref([]);
const isApplyingRecommendations = ref(false);

const agentCapacityPolicies = useMapGetter(
  'agentCapacityPolicies/getAgentCapacityPolicies'
);
const uiFlags = useMapGetter('agentCapacityPolicies/getUIFlags');
const usersUiFlags = useMapGetter('agentCapacityPolicies/getUsersUIFlags');

const confirmDeletePolicyDialogRef = ref(null);

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE'),
      routeName: 'assignment_policy_index',
    },
    {
      label: t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.TITLE'),
    },
  ];
  return items;
});

const handleBreadcrumbClick = item => {
  router.push({
    name: item.routeName,
  });
};

const onClickCreatePolicy = () => {
  router.push({
    name: 'agent_capacity_policy_create',
  });
};

const onClickEditPolicy = id => {
  router.push({
    name: 'agent_capacity_policy_edit',
    params: {
      id,
    },
  });
};

const handleFetchUsers = id => {
  if (usersUiFlags.value.isFetching) return;
  store.dispatch('agentCapacityPolicies/getUsers', id);
};

const handleDelete = id => {
  confirmDeletePolicyDialogRef.value.openDialog(id);
};

const handleDeletePolicy = async policyId => {
  try {
    await store.dispatch('agentCapacityPolicies/delete', policyId);
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.DELETE_POLICY.SUCCESS_MESSAGE')
    );
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.DELETE_POLICY.ERROR_MESSAGE')
    );
  }
};

const recommendationsUrl = computed(
  () =>
    `/api/v1/accounts/${route.params.accountId}/agent_capacity_policies/recommendations`
);

const fetchRecommendations = async () => {
  try {
    const response = await window.axios.get(recommendationsUrl.value);
    recommendations.value = response.data.recommendations;
  } catch {
    useAlert(t('WORKFORCE_AUTOPILOT.ERROR'));
  }
};

const applyRecommendations = async () => {
  isApplyingRecommendations.value = true;
  try {
    await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/agent_capacity_policies/apply_recommendations`
    );
    await store.dispatch('agentCapacityPolicies/get');
    autopilotDialogRef.value?.close();
    useAlert(t('WORKFORCE_AUTOPILOT.SUCCESS'));
  } catch {
    useAlert(t('WORKFORCE_AUTOPILOT.ERROR'));
  } finally {
    isApplyingRecommendations.value = false;
  }
};

onMounted(() => {
  store.dispatch('agentCapacityPolicies/get');
  if (isStacklab) fetchRecommendations();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :no-records-found="false"
    :no-records-message="
      $t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.NO_RECORDS_FOUND')
    "
  >
    <template #header>
      <div class="flex items-center gap-2 w-full justify-between min-h-10">
        <Breadcrumb :items="breadcrumbItems" @click="handleBreadcrumbClick" />
        <Button icon="i-lucide-plus" md @click="onClickCreatePolicy">
          {{
            $t(
              'ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.CREATE_POLICY'
            )
          }}
        </Button>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pt-4">
        <section
          v-if="isStacklab"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-5"
        >
          <div
            class="flex flex-col justify-between gap-4 sm:flex-row sm:items-center"
          >
            <div>
              <div class="flex items-center gap-2">
                <span class="i-lucide-gauge size-5 text-n-brand" />
                <h2 class="text-base font-medium text-n-slate-12">
                  {{ t('WORKFORCE_AUTOPILOT.TITLE') }}
                </h2>
              </div>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('WORKFORCE_AUTOPILOT.DESCRIPTION') }}
              </p>
            </div>
            <Button
              color="slate"
              variant="outline"
              :label="t('WORKFORCE_AUTOPILOT.REVIEW')"
              :disabled="!recommendations.length"
              @click="autopilotDialogRef?.open()"
            />
          </div>
        </section>
        <section
          v-else
          class="flex min-h-40 items-center justify-center rounded-xl border border-n-weak bg-n-surface-2 p-5"
        >
          <div class="max-w-md p-2 text-center">
            <span
              class="i-lucide-lock mb-3 inline-block size-10 text-n-slate-10"
            />
            <h2 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ t('WORKFORCE_AUTOPILOT.LICENSE_REQUIRED') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('WORKFORCE_AUTOPILOT.LICENSE_DESCRIPTION') }}
            </p>
          </div>
        </section>
        <AgentCapacityPolicyCard
          v-for="policy in agentCapacityPolicies"
          :key="policy.id"
          v-bind="policy"
          :is-fetching-users="usersUiFlags.isFetching"
          @fetch-users="handleFetchUsers"
          @edit="onClickEditPolicy"
          @delete="handleDelete"
        />
      </div>
    </template>
    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
    <Dialog
      ref="autopilotDialogRef"
      width="2xl"
      :title="t('WORKFORCE_AUTOPILOT.DIALOG_TITLE')"
      :description="t('WORKFORCE_AUTOPILOT.DIALOG_DESCRIPTION')"
      :confirm-button-label="t('WORKFORCE_AUTOPILOT.APPLY')"
      :is-loading="isApplyingRecommendations"
      @confirm="applyRecommendations"
    >
      <div class="max-h-96 space-y-2 overflow-y-auto">
        <div
          v-for="item in recommendations"
          :key="item.inbox_id"
          class="flex items-center justify-between gap-4 rounded-lg border border-n-weak p-3"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ item.inbox_name }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                t('WORKFORCE_AUTOPILOT.INBOX_STATS', {
                  open: item.open_conversations,
                  daily: item.daily_volume,
                  agents: item.agent_count,
                })
              }}
            </p>
          </div>
          <div class="text-right">
            <p class="text-lg font-semibold text-n-slate-12">
              {{ item.recommended_limit }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('WORKFORCE_AUTOPILOT.PER_AGENT') }}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  </SettingsLayout>
</template>
